<script setup lang="ts">
import {
  AddPermissionManagerRule,
  CreateRole,
  CreateRoleProfile,
  CreateUserPermission,
  DeleteUserPermission,
  GetPageAccessSettings,
  GetPermissionManagerOptions,
  GetPermissionManagerPermissions,
  GetRoleProfiles,
  GetRoles,
  GetUserPermissions,
  GetUsers,
  UpdatePermissionManagerRule,
  UpdateRoleProfile,
  UpdateUserRoleAssignments,
  type CreateRolePayload,
  type CreateUserPermissionPayload,
  type FrappeDocTypePermission,
  type FrappeRole,
  type FrappeRoleProfile,
  type FrappeUser,
  type FrappeUserPermission,
  type PageAccessSetting,
  type PermissionManagerOption,
  type PermissionManagerPermissionKey,
} from "@/api/permissionsApi";
import {
  searchFrappeDoctype,
  type FrappeDoctypeLookupOption,
} from "@/api/frappeLookupApi";
import FrappeDoctypeAutocomplete from "@/components/shared/FrappeDoctypeAutocomplete.vue";
import { hasFullAccessRole } from "@/config/permissionManifest";
import { resolveFrappeFileUrl } from "@/services/frappe/files";
import useToastify from "@/stores/toastify.store";

definePage({
  meta: {
    action: "read",
    subject: "Access Designer",
    permissionKey: "page.users.access_designer",
  },
});

type PermissionActionPreview = {
  key: PermissionManagerPermissionKey;
  icon: string;
  color: string;
};

type PermissionActionState = PermissionActionPreview & {
  allowed: boolean;
  ownerOnly: boolean;
};

type RoleTableSummary = {
  table: string;
  label: string;
  actions: PermissionManagerPermissionKey[];
  ownerOnly: boolean;
};

const { t } = useI18n();
const toast = useToastify();
const ad = (key: string, args?: Record<string, unknown>) => t(`alkokh.accessDesigner.${key}`, args ?? {});
const am = (key: string, args?: Record<string, unknown>) => t(`alkokh.accessManagement.${key}`, args ?? {});

const loading = ref(false);
const saving = ref(false);
const tableLoading = ref(false);
const roleSaving = ref(false);
const permissionSaving = ref(false);
const roleTableSummaryLoading = ref(false);

const users = ref<FrappeUser[]>([]);
const roleProfiles = ref<FrappeRoleProfile[]>([]);
const roles = ref<FrappeRole[]>([]);
const userPermissions = ref<FrappeUserPermission[]>([]);
const pageAccessSettings = ref<PageAccessSetting[]>([]);
const permissionTables = ref<PermissionManagerOption[]>([]);
const selectedTable = ref<string | null>(null);
const selectedTableRole = ref<string | null>(null);
const tablePermissions = ref<FrappeDocTypePermission[]>([]);
const roleTablePanel = ref<number | null>(null);
const roleTableSummaries = ref<RoleTableSummary[]>([]);
const roleTableSummaryLoadedFor = ref("");
const lastPermissionChange = ref<{
  table: string;
  role: string;
  action: PermissionManagerPermissionKey;
  enabled: boolean;
} | null>(null);

const userPermissionValueLabels = ref<Record<string, string>>({});
const userPermissionDialog = ref(false);
const userPermissionSaving = ref(false);
const editingUserPermission = ref<FrappeUserPermission | null>(null);
const userPermissionForm = ref<CreateUserPermissionPayload>({
  user: "",
  allow: "Warehouse",
  for_value: "",
  apply_to_all_doctypes: 1,
});
const allowedTableOption = ref<FrappeDoctypeLookupOption | null>(null);
const allowedValueOption = ref<FrappeDoctypeLookupOption | null>(null);
const userPermissionToDelete = ref<FrappeUserPermission | null>(null);
const deleteUserPermissionDialog = ref(false);
const userPermissionDeleting = ref(false);
let prefillingUserPermission = false;

const ALLOWED_VALUE_LABEL_FIELDS: Record<string, string> = {
  CategoryCareServices: "category_name",
  "CareService template": "service_name",
  "Healthcare Practitioner": "practitioner_name",
  Guardian: "full_name",
  Customer: "customer_name",
  Item: "item_name",
  Pet: "pet_name",
  Warehouse: "warehouse_name",
  Company: "company_name",
  CareService: "service_name",
};

const allowedValueLabelField = computed(() =>
  ALLOWED_VALUE_LABEL_FIELDS[userPermissionForm.value.allow ?? ""] ?? "name",
);

const allowedValueSearchFields = computed(() => {
  const labelField = allowedValueLabelField.value;

  return labelField === "name" ? ["name"] : ["name", labelField];
});

const userSearch = ref("");
const profileSearch = ref("");
const selectedUser = ref<FrappeUser | null>(null);
const draftRoleProfiles = ref<string[]>([]);
const draftRoles = ref<string[]>([]);
const roleProfilePage = ref(1);
const roleProfilesPerPage = 6;
const roleDialog = ref(false);
const roleForm = ref<CreateRolePayload>({
  role_name: "",
  desk_access: 1,
  disabled: 0,
  home_page: "",
  restrict_to_domain: "",
});

const roleProfileDialog = ref(false);
const roleProfileSaving = ref(false);
const roleProfileForm = ref<{ role_profile: string; roles: string[] }>({
  role_profile: "",
  roles: [],
});
// When set, the role-profile dialog is editing an existing profile instead of creating one.
const editingRoleProfile = ref<FrappeRoleProfile | null>(null);

// Right-click context menu state for profile cards.
const profileMenu = ref(false);
const profileMenuTarget = ref<FrappeRoleProfile | null>(null);
const profileMenuX = ref(0);
const profileMenuY = ref(0);

const permissionActions: PermissionActionPreview[] = [
  { key: "select", icon: "tabler-list-check", color: "primary" },
  { key: "read", icon: "tabler-eye", color: "info" },
  { key: "write", icon: "tabler-pencil", color: "primary" },
  { key: "create", icon: "tabler-plus", color: "success" },
  { key: "delete", icon: "tabler-trash", color: "error" },
  { key: "submit", icon: "tabler-circle-check", color: "success" },
  { key: "cancel", icon: "tabler-circle-x", color: "warning" },
  { key: "print", icon: "tabler-printer", color: "secondary" },
  { key: "export", icon: "tabler-file-export", color: "info" },
  { key: "import", icon: "tabler-file-import", color: "primary" },
  { key: "share", icon: "tabler-share", color: "secondary" },
];

const statCards = computed(() => [
  {
    icon: "tabler-users",
    color: "primary",
    label: ad("stats.users"),
    value: users.value.length,
  },
  {
    icon: "tabler-id-badge-2",
    color: "success",
    label: ad("stats.profiles"),
    value: frontRoleProfiles.value.length,
  },
  {
    icon: "tabler-shield-check",
    color: "info",
    label: ad("stats.roles"),
    value: roles.value.length,
  },
  {
    icon: "tabler-layout-grid",
    color: "warning",
    label: ad("stats.pages"),
    value: pageAccessSettings.value.length,
  },
]);

const roleOptions = computed(() =>
  roles.value
    .filter(role => Number(role.disabled ?? 0) !== 1)
    .map(role => role.name)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b)),
);

// Access Designer only manages role profiles created/flagged from this app (custom_is_front = 1).
const frontRoleProfiles = computed(() =>
  roleProfiles.value.filter(profile => Number(profile.custom_is_front ?? 0) === 1),
);

const roleProfileOptions = computed(() =>
  frontRoleProfiles.value
    .map(profile => getProfileName(profile))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b)),
);

// The inspector autocomplete must list every profile already on the user (even
// non-front/legacy ones), otherwise VAutocomplete drops the chip for any v-model
// value missing from :items and the assigned profile silently disappears.
const inspectorRoleProfileOptions = computed(() =>
  [...new Set([...roleProfileOptions.value, ...draftRoleProfiles.value])]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b)),
);

const filteredUsers = computed(() => {
  const query = userSearch.value.trim().toLowerCase();
  if (!query)
    return users.value;

  return users.value.filter((user) => {
    const haystack = [
      getUserLabel(user),
      user.name,
      user.email,
      user.username,
      ...getUserRoleProfiles(user),
      ...getUserRoles(user),
    ].join(" ").toLowerCase();

    return haystack.includes(query);
  });
});

const filteredRoleProfiles = computed(() => {
  const query = profileSearch.value.trim().toLowerCase();
  if (!query)
    return frontRoleProfiles.value;

  return frontRoleProfiles.value.filter((profile) => {
    const haystack = [
      getProfileName(profile),
      ...getProfileRoles(profile),
    ].join(" ").toLowerCase();

    return haystack.includes(query);
  });
});

const roleProfilePageCount = computed(() =>
  Math.max(1, Math.ceil(filteredRoleProfiles.value.length / roleProfilesPerPage)),
);

const paginatedRoleProfiles = computed(() => {
  const start = (roleProfilePage.value - 1) * roleProfilesPerPage;

  return filteredRoleProfiles.value.slice(start, start + roleProfilesPerPage);
});

const selectedUserKey = computed(() => selectedUser.value?.name ?? "");
const selectedUserProfiles = computed(() => selectedUser.value ? getUserRoleProfiles(selectedUser.value) : []);
const selectedUserRoles = computed(() => selectedUser.value ? getUserRoles(selectedUser.value) : []);
const selectedUserPermissions = computed(() => {
  if (!selectedUser.value)
    return [];

  const userIds = new Set([
    selectedUser.value.name,
    selectedUser.value.email,
  ].filter(Boolean));

  return userPermissions.value.filter(row => userIds.has(row.user));
});

const resolvedDraftRoles = computed(() => {
  const resolved = new Set(draftRoles.value);
  const selectedProfiles = new Set(draftRoleProfiles.value);

  roleProfiles.value.forEach((profile) => {
    const profileName = getProfileName(profile);
    if (!selectedProfiles.has(profileName) && !selectedProfiles.has(profile.name))
      return;

    getProfileRoles(profile).forEach(role => resolved.add(role));
  });

  return [...resolved].filter(Boolean).sort((a, b) => a.localeCompare(b));
});

const tableRoleOptions = computed(() =>
  resolvedDraftRoles.value.map(role => ({
    title: role,
    value: role,
  })),
);

const hasChanges = computed(() =>
  !sameStringSet(draftRoleProfiles.value, selectedUserProfiles.value)
  || !sameStringSet(draftRoles.value, selectedUserRoles.value),
);

const pagePreviewCards = computed(() => {
  const draftRolesSet = new Set(resolvedDraftRoles.value);
  const canSeeEverything = hasFullAccessRole(resolvedDraftRoles.value);

  return pageAccessSettings.value
    .map((page) => {
      const enabled = Number(page.enabled ?? 0) === 1;
      const allowed = enabled && (canSeeEverything || (page.roles ?? []).some(role => draftRolesSet.has(role)));

      return {
        ...page,
        allowed,
      };
    })
    .sort((a, b) => Number(b.allowed) - Number(a.allowed) || a.label.localeCompare(b.label));
});

const allowedPageCount = computed(() => pagePreviewCards.value.filter(page => page.allowed).length);

const selectedTablePermissionRows = computed(() =>
  tablePermissions.value.filter(row =>
    String(row.role || "").trim() === String(selectedTableRole.value || "").trim(),
  ),
);

const editableTablePermissionRow = computed(() =>
  tablePermissions.value
    .filter(row => String(row.role || "").trim() === String(selectedTableRole.value || "").trim())
    .sort((a, b) =>
      Number(a.permlevel ?? 0) - Number(b.permlevel ?? 0)
      || Number(a.if_owner ?? 0) - Number(b.if_owner ?? 0),
    )[0] ?? null,
);

const tablePermissionSummary = computed<PermissionActionState[]>(() =>
  permissionActions.map(action => ({
    ...action,
    allowed: selectedTablePermissionRows.value.some(row => Number(row[action.key] ?? 0) === 1),
    ownerOnly: selectedTablePermissionRows.value.some(row =>
      Number(row[action.key] ?? 0) === 1 && Number(row.if_owner ?? 0) === 1,
    ),
  })),
);

const selectedRoleAllowedActionCount = computed(() =>
  tablePermissionSummary.value.filter(action => action.allowed).length,
);

const selectedTableLabel = computed(() =>
  permissionTables.value.find(table => table.value === selectedTable.value)?.label
  || selectedTable.value
  || "",
);

const roleTableSummaryTitle = computed(() =>
  selectedTableRole.value
    ? ad("tablePreview.roleTablesTitle", { role: selectedTableRole.value })
    : ad("tablePreview.roleTablesTitleEmpty"),
);

function getUserLabel(user: FrappeUser) {
  return user.full_name || user.email || user.name;
}

function userPermissionValueLabelKey(doctype?: string | null, value?: string | null) {
  return `${String(doctype || "").trim()}::${String(value || "").trim()}`;
}

function displayUserPermissionValue(row?: FrappeUserPermission | null) {
  if (!row)
    return "";

  return userPermissionValueLabels.value[userPermissionValueLabelKey(row.allow, row.for_value)]
    || row.for_value
    || "";
}

async function resolveUserPermissionValueLabels(rows: FrappeUserPermission[]) {
  const grouped = new Map<string, Set<string>>();

  rows.forEach((row) => {
    const doctype = String(row.allow || "").trim();
    const value = String(row.for_value || "").trim();
    const labelField = ALLOWED_VALUE_LABEL_FIELDS[doctype];

    if (!doctype || !value || !labelField || labelField === "name")
      return;

    if (!grouped.has(doctype))
      grouped.set(doctype, new Set());

    grouped.get(doctype)!.add(value);
  });

  if (!grouped.size)
    return;

  const nextLabels = { ...userPermissionValueLabels.value };

  await Promise.all([...grouped.entries()].map(async ([doctype, values]) => {
    const ids = [...values];
    const labelField = ALLOWED_VALUE_LABEL_FIELDS[doctype];

    try {
      const result = await searchFrappeDoctype(ref(false), {
        doctype,
        fields: [labelField],
        labelField,
        searchFields: ["name", labelField],
        baseFilters: [[doctype, "name", "in", ids]],
        limit: Math.max(ids.length, 1),
        orderBy: "name asc",
      });

      result.items.forEach((item) => {
        nextLabels[userPermissionValueLabelKey(doctype, item.id)] = item.label;
      });
    } catch (error) {
      console.warn(`Failed to resolve ${doctype} permission labels`, error);
    }
  }));

  userPermissionValueLabels.value = nextLabels;
}

function getUserRoleProfiles(user: FrappeUser) {
  return (user.role_profiles ?? [])
    .map(row => row.role_profile)
    .filter(Boolean);
}

function getUserRoles(user: FrappeUser) {
  return (user.roles ?? [])
    .map(row => row.role)
    .filter(Boolean);
}

function getProfileName(profile: FrappeRoleProfile) {
  return profile.role_profile || profile.name;
}

function getProfileRoles(profile: FrappeRoleProfile) {
  return (profile.roles ?? [])
    .map(row => row.role)
    .filter(Boolean);
}

function avatarInitial(value: string) {
  return (value.trim().charAt(0) || "?").toUpperCase();
}

function sameStringSet(left: string[], right: string[]) {
  const normalize = (values: string[]) => [...new Set(values.filter(Boolean))].sort();
  const a = normalize(left);
  const b = normalize(right);

  return a.length === b.length && a.every((value, index) => value === b[index]);
}

function selectUser(user: FrappeUser) {
  selectedUser.value = user;
  revertDraft();
}

function revertDraft() {
  draftRoleProfiles.value = [...selectedUserProfiles.value];
  draftRoles.value = [...selectedUserRoles.value];
  syncSelectedTableRole();
}

function toggleDraftProfile(profile: FrappeRoleProfile) {
  if (!selectedUser.value)
    return;

  const profileName = getProfileName(profile);
  const exists = draftRoleProfiles.value.includes(profileName);

  draftRoleProfiles.value = exists
    ? draftRoleProfiles.value.filter(item => item !== profileName)
    : [...draftRoleProfiles.value, profileName];
}

function profileUserCount(profile: FrappeRoleProfile) {
  const profileName = getProfileName(profile);

  return users.value.filter(user => getUserRoleProfiles(user).includes(profileName)).length;
}

function hasPermissionFlag(row: FrappeDocTypePermission, key: PermissionManagerPermissionKey) {
  return Number(row[key] ?? 0) === 1;
}

function getEnabledPermissionActions(rows: FrappeDocTypePermission[]) {
  return permissionActions
    .filter(action => rows.some(row => Number(row[action.key] ?? 0) === 1))
    .map(action => action.key);
}

function pageIcon(page: PageAccessSetting) {
  if (page.doctype)
    return "tabler-database";

  if ((page.paths ?? []).some(path => path.includes("setting")))
    return "tabler-settings";

  return "tabler-layout-sidebar";
}

async function loadTablePermissions() {
  if (!selectedTable.value) {
    tablePermissions.value = [];

    return;
  }

  tableLoading.value = true;

  try {
    tablePermissions.value = await GetPermissionManagerPermissions(selectedTable.value, selectedTableRole.value);
  } catch (error) {
    console.error(error);
    tablePermissions.value = [];
  } finally {
    tableLoading.value = false;
  }
}

async function loadRoleTableSummaries(force = false) {
  if (!selectedTableRole.value || !permissionTables.value.length) {
    roleTableSummaries.value = [];
    roleTableSummaryLoadedFor.value = "";

    return;
  }

  const role = selectedTableRole.value;
  if (!force && roleTableSummaryLoadedFor.value === role)
    return;

  roleTableSummaryLoading.value = true;

  try {
    const rows = await Promise.all(
      permissionTables.value.map(async (table) => {
        const permissions = await GetPermissionManagerPermissions(table.value, role);
        const actions = getEnabledPermissionActions(permissions);

        if (!actions.length)
          return null;

        return {
          table: table.value,
          label: table.label || table.value,
          actions,
          ownerOnly: permissions.some(row =>
            Number(row.if_owner ?? 0) === 1
            && actions.some(action => Number(row[action] ?? 0) === 1),
          ),
        };
      }),
    );

    roleTableSummaries.value = rows
      .filter((row): row is RoleTableSummary => Boolean(row))
      .sort((a, b) => a.label.localeCompare(b.label));
    roleTableSummaryLoadedFor.value = role;
  } finally {
    roleTableSummaryLoading.value = false;
  }
}

function handleRoleTablePanel(value: unknown) {
  if (value === null || value === undefined || value === false)
    return;

  loadRoleTableSummaries();
}

function patchLocalTablePermission(
  role: string,
  action: PermissionManagerPermissionKey,
  value: 0 | 1,
) {
  const existingRows = tablePermissions.value.filter(row =>
    String(row.role || "").trim() === String(role || "").trim(),
  );

  if (!existingRows.length) {
    tablePermissions.value = [
      ...tablePermissions.value,
      {
        role,
        permlevel: 0,
        if_owner: 0,
        [action]: value,
      },
    ];

    return;
  }

  const rowToPatch = editableTablePermissionRow.value ?? existingRows[0];

  tablePermissions.value = tablePermissions.value.map((row) => {
    if (row !== rowToPatch)
      return row;

    return {
      ...row,
      [action]: value,
    };
  });
}

function resetRoleForm() {
  roleForm.value = {
    role_name: "",
    desk_access: 1,
    disabled: 0,
    home_page: "",
    restrict_to_domain: "",
  };
}

function openRoleDialog() {
  resetRoleForm();
  roleDialog.value = true;
}

function openRoleProfileDialog() {
  editingRoleProfile.value = null;
  roleProfileForm.value = { role_profile: "", roles: [] };
  roleProfileDialog.value = true;
}

function openProfileContextMenu(event: MouseEvent, profile: FrappeRoleProfile) {
  event.preventDefault();
  profileMenuTarget.value = profile;
  profileMenuX.value = event.clientX;
  profileMenuY.value = event.clientY;
  profileMenu.value = true;
}

function openEditRoleProfileDialog(profile: FrappeRoleProfile | null) {
  if (!profile)
    return;

  editingRoleProfile.value = profile;
  roleProfileForm.value = {
    role_profile: getProfileName(profile),
    roles: getProfileRoles(profile),
  };
  profileMenu.value = false;
  roleProfileDialog.value = true;
}

async function saveRoleProfile() {
  const profileName = roleProfileForm.value.role_profile.trim();

  if (!profileName) {
    toast.error({ text: ad("messages.roleProfileNameRequired") });

    return;
  }

  roleProfileSaving.value = true;

  const profileToEdit = editingRoleProfile.value;

  try {
    const payload = {
      role_profile: profileName,
      roles: roleProfileForm.value.roles
        .filter(Boolean)
        .map(role => ({ role })),
      // Flag profiles created/managed from this app.
      custom_is_front: 1 as const,
    };

    if (profileToEdit) {
      await UpdateRoleProfile(profileToEdit.name, payload);
    } else {
      await CreateRoleProfile(payload);
    }

    roleProfiles.value = await GetRoleProfiles();

    // Pre-assign a newly-created profile to the user being edited so the change is one click away.
    if (!profileToEdit && selectedUser.value && !draftRoleProfiles.value.includes(profileName))
      draftRoleProfiles.value = [...draftRoleProfiles.value, profileName];

    roleProfileDialog.value = false;
    editingRoleProfile.value = null;
    toast.success({
      text: profileToEdit
        ? ad("messages.roleProfileUpdated")
        : ad("messages.roleProfileCreated"),
    });
  } catch (error: any) {
    toast.error({
      text: error?.message
        || (profileToEdit
          ? ad("messages.roleProfileUpdateFailed")
          : ad("messages.roleProfileCreateFailed")),
    });
  } finally {
    roleProfileSaving.value = false;
  }
}

async function saveRole() {
  const roleName = roleForm.value.role_name.trim();

  if (!roleName) {
    toast.error({ text: ad("messages.roleNameRequired") });

    return;
  }

  roleSaving.value = true;

  try {
    await CreateRole({
      role_name: roleName,
      desk_access: Number(roleForm.value.desk_access ?? 0) === 1 ? 1 : 0,
      disabled: Number(roleForm.value.disabled ?? 0) === 1 ? 1 : 0,
      home_page: roleForm.value.home_page?.trim() || null,
      restrict_to_domain: roleForm.value.restrict_to_domain?.trim() || null,
    });

    roles.value = await GetRoles();
    const permissionOptions = await GetPermissionManagerOptions();
    permissionTables.value = permissionOptions.tables;

    if (selectedUser.value && !draftRoles.value.includes(roleName))
      draftRoles.value = [...draftRoles.value, roleName];

    selectedTableRole.value = roleName;
    roleDialog.value = false;
    toast.success({ text: ad("messages.roleCreated") });
  } catch (error: any) {
    toast.error({ text: error?.message || ad("messages.roleCreateFailed") });
  } finally {
    roleSaving.value = false;
  }
}

function syncSelectedTableRole() {
  if (selectedTableRole.value && resolvedDraftRoles.value.includes(selectedTableRole.value))
    return;

  selectedTableRole.value = resolvedDraftRoles.value[0] ?? null;
}

async function toggleTablePermission(action: PermissionManagerPermissionKey) {
  if (!selectedTable.value || !selectedTableRole.value)
    return;

  const currentRow = editableTablePermissionRow.value;
  const nextValue = currentRow && hasPermissionFlag(currentRow, action) ? 0 : 1;
  const previousRows = [...tablePermissions.value];

  permissionSaving.value = true;
  patchLocalTablePermission(selectedTableRole.value, action, nextValue);

  try {
    if (!currentRow)
      await AddPermissionManagerRule(selectedTable.value, selectedTableRole.value, 0);

    await UpdatePermissionManagerRule(
      selectedTable.value,
      selectedTableRole.value,
      Number(currentRow?.permlevel ?? 0),
      action,
      nextValue,
      Number(currentRow?.if_owner ?? 0) === 1 ? 1 : 0,
    );

    await loadTablePermissions();
    if (roleTableSummaryLoadedFor.value === selectedTableRole.value)
      await loadRoleTableSummaries(true);

    lastPermissionChange.value = {
      table: selectedTableLabel.value,
      role: selectedTableRole.value,
      action,
      enabled: nextValue === 1,
    };
    toast.success({ text: ad("messages.tablePermissionUpdated") });
  } catch (error: any) {
    tablePermissions.value = previousRows;
    toast.error({ text: error?.message || ad("messages.tablePermissionUpdateFailed") });
  } finally {
    permissionSaving.value = false;
  }
}

function openUserPermissionDialog(row?: FrappeUserPermission) {
  if (!selectedUser.value)
    return;

  prefillingUserPermission = true;

  if (row) {
    editingUserPermission.value = row;
    userPermissionForm.value = {
      user: row.user,
      allow: row.allow,
      for_value: row.for_value,
      apply_to_all_doctypes: Number(row.apply_to_all_doctypes ?? 0) === 1 ? 1 : 0,
      applicable_for: row.applicable_for ?? null,
    };
    allowedTableOption.value = {
      id: row.allow,
      label: row.allow,
      subtitle: null,
      image: null,
      raw: { name: row.allow },
    };
    allowedValueOption.value = {
      id: row.for_value,
      label: displayUserPermissionValue(row) || row.for_value,
      subtitle: null,
      image: null,
      raw: { name: row.for_value },
    };
  } else {
    editingUserPermission.value = null;
    userPermissionForm.value = {
      user: selectedUser.value.name,
      allow: "Warehouse",
      for_value: "",
      apply_to_all_doctypes: 1,
    };
    allowedTableOption.value = {
      id: "Warehouse",
      label: "Warehouse",
      subtitle: null,
      image: null,
      raw: { name: "Warehouse" },
    };
    allowedValueOption.value = null;
  }

  userPermissionDialog.value = true;
  nextTick(() => {
    prefillingUserPermission = false;
  });
}

async function saveUserPermission() {
  if (!selectedUser.value)
    return;

  const forValue = allowedValueOption.value?.id || userPermissionForm.value.for_value;

  const payload: CreateUserPermissionPayload = {
    user: String(userPermissionForm.value.user || selectedUser.value.name).trim(),
    allow: String(userPermissionForm.value.allow || "").trim(),
    for_value: String(forValue || "").trim(),
    apply_to_all_doctypes: Number(userPermissionForm.value.apply_to_all_doctypes ?? 0) === 1 ? 1 : 0,
  };

  if (!payload.user || !payload.allow || !payload.for_value) {
    toast.error({ text: am("messages.userPermissionRequired") });

    return;
  }

  userPermissionSaving.value = true;

  try {
    await CreateUserPermission(payload);

    // Editing replaces the previous restriction: create the new row first, then drop the old one.
    if (editingUserPermission.value)
      await DeleteUserPermission(editingUserPermission.value.name);

    toast.success({
      text: editingUserPermission.value
        ? ad("messages.userPermissionUpdated")
        : am("messages.userPermissionAdded"),
    });
    userPermissionDialog.value = false;
    editingUserPermission.value = null;
    await loadUserPermissions();
  } catch (error: any) {
    toast.error({ text: error?.message || ad("messages.userPermissionFailed") });
  } finally {
    userPermissionSaving.value = false;
  }
}

function openDeleteUserPermissionDialog(row: FrappeUserPermission) {
  userPermissionToDelete.value = row;
  deleteUserPermissionDialog.value = true;
}

async function confirmDeleteUserPermission() {
  if (!userPermissionToDelete.value)
    return;

  userPermissionDeleting.value = true;

  try {
    await DeleteUserPermission(userPermissionToDelete.value.name);
    toast.success({ text: am("messages.userPermissionRemoved") });
    deleteUserPermissionDialog.value = false;
    userPermissionToDelete.value = null;
    await loadUserPermissions();
  } catch (error: any) {
    toast.error({ text: error?.message || ad("messages.userPermissionFailed") });
  } finally {
    userPermissionDeleting.value = false;
  }
}

async function loadUserPermissions() {
  userPermissions.value = await GetUserPermissions();
  await resolveUserPermissionValueLabels(userPermissions.value);
}

async function refreshAll() {
  loading.value = true;

  try {
    const [
      usersResponse,
      profilesResponse,
      rolesResponse,
      userPermissionsResponse,
      pagesResponse,
      permissionOptions,
    ] = await Promise.all([
      GetUsers(),
      GetRoleProfiles(),
      GetRoles(),
      GetUserPermissions(),
      GetPageAccessSettings(),
      GetPermissionManagerOptions(),
    ]);

    users.value = usersResponse;
    roleProfiles.value = profilesResponse;
    roles.value = rolesResponse;
    userPermissions.value = userPermissionsResponse;
    pageAccessSettings.value = pagesResponse;
    permissionTables.value = permissionOptions.tables;

    await resolveUserPermissionValueLabels(userPermissions.value);

    if (!selectedTable.value)
      selectedTable.value = permissionTables.value.find(item => item.value === "User")?.value
        || permissionTables.value[0]?.value
        || null;

    if (!selectedUser.value && users.value.length)
      selectUser(users.value[0]);
    else if (selectedUser.value) {
      const refreshedUser = users.value.find(user => user.name === selectedUserKey.value);
      if (refreshedUser)
        selectUser(refreshedUser);
    }

    await loadTablePermissions();
  } finally {
    loading.value = false;
  }
}

async function saveAssignments() {
  if (!selectedUser.value)
    return;

  saving.value = true;

  try {
    await UpdateUserRoleAssignments(
      selectedUser.value.name,
      draftRoleProfiles.value.map(roleProfile => ({ role_profile: roleProfile })),
      draftRoles.value.map(role => ({ role })),
    );

    toast.success({ text: ad("messages.saved") });
    await refreshAll();
  } catch (error: any) {
    toast.error({ text: error?.message || ad("messages.saveFailed") });
  } finally {
    saving.value = false;
  }
}

watch([selectedTable, selectedTableRole], loadTablePermissions);
watch(selectedTableRole, () => {
  roleTablePanel.value = null;
  roleTableSummaries.value = [];
  roleTableSummaryLoadedFor.value = "";
});
watch(profileSearch, () => {
  roleProfilePage.value = 1;
});
watch(filteredRoleProfiles, () => {
  if (roleProfilePage.value > roleProfilePageCount.value)
    roleProfilePage.value = roleProfilePageCount.value;
});
watch(resolvedDraftRoles, syncSelectedTableRole);

watch(allowedTableOption, (option) => {
  userPermissionForm.value.allow = option?.id || "";
});

watch(() => userPermissionForm.value.allow, (next, prev) => {
  if (next === prev || prefillingUserPermission)
    return;

  userPermissionForm.value.for_value = "";
  allowedValueOption.value = null;
});

onMounted(refreshAll);

/** Phone gate. Always `useMobileShell()`, never raw `useDisplay()` — it carries the kill switch. */
const { isMobileShell } = useMobileShell();
</script>

<template>
  <section class="access-designer">
    <div class="designer-hero d-flex flex-wrap align-center justify-space-between gap-4 mb-5">
      <div class="d-flex align-center gap-3">
        <VAvatar color="primary" variant="tonal" rounded size="42">
          <VIcon icon="tabler-sparkles" size="24" />
        </VAvatar>
        <div class="min-w-0">
          <div class="text-overline text-primary font-weight-bold">
            {{ ad("eyebrow") }}
          </div>
          <h1 class="text-h5 mb-1">
            {{ ad("title") }}
          </h1>
          <p class="text-body-2 text-medium-emphasis mb-0 designer-subtitle">
            {{ ad("subtitle") }}
          </p>
        </div>
      </div>

      <div class="d-flex align-center gap-2">
        <VBtn
          variant="tonal"
          color="secondary"
          prepend-icon="tabler-shield-lock"
          :to="{ name: 'permission' }"
        >
          {{ ad("actions.advanced") }}
        </VBtn>
        <VBtn
          color="primary"
          prepend-icon="tabler-refresh"
          :loading="loading"
          @click="refreshAll"
        >
          {{ ad("actions.refresh") }}
        </VBtn>
      </div>
    </div>

    <VRow class="mb-5" dense>
      <VCol
        v-for="card in statCards"
        :key="card.label"
        cols="12"
        sm="6"
        lg="3"
      >
        <VCard class="access-designer-stat" variant="tonal" :color="card.color">
          <VCardText class="d-flex align-center gap-3 py-4">
            <VAvatar :color="card.color" variant="flat" rounded size="38">
              <VIcon :icon="card.icon" size="21" />
            </VAvatar>
            <div>
              <div class="text-h6 font-weight-semibold">
                {{ card.value }}
              </div>
              <div class="text-body-2">
                {{ card.label }}
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <VRow dense>
      <VCol cols="12" lg="3">
        <VCard class="designer-panel">
          <VCardText class="pa-5">
            <div class="d-flex align-center justify-space-between gap-3 mb-3">
              <div class="min-w-0">
                <h2 class="text-subtitle-1 font-weight-bold mb-1">
                  {{ ad("users.title") }}
                </h2>
                <p class="text-caption text-medium-emphasis mb-0">
                  {{ ad("users.subtitle") }}
                </p>
              </div>
              <VAvatar color="primary" variant="tonal" rounded size="34">
                <VIcon icon="tabler-users" />
              </VAvatar>
            </div>

            <VTextField
              v-model="userSearch"
              :label="ad('users.search')"
              prepend-inner-icon="tabler-search"
              variant="outlined"
              density="comfortable"
              rounded="lg"
              clearable
              hide-details
              class="designer-input mb-4"
            />

            <div class="designer-user-list">
              <button
                v-for="user in filteredUsers"
                :key="user.name"
                type="button"
                class="designer-user-card"
                :class="{ 'is-selected': selectedUser?.name === user.name }"
                @click="selectUser(user)"
              >
                <VAvatar size="38" color="secondary" variant="tonal">
                  <VImg
                    v-if="user.user_image"
                    :src="resolveFrappeFileUrl(user.user_image)"
                  />
                  <span v-else>{{ avatarInitial(getUserLabel(user)) }}</span>
                </VAvatar>
                <div class="min-w-0 flex-grow-1 text-start">
                  <div class="font-weight-semibold text-truncate">
                    {{ getUserLabel(user) }}
                  </div>
                  <div class="text-caption text-medium-emphasis text-truncate">
                    {{ user.email || user.name }}
                  </div>
                  <div class="d-flex flex-wrap gap-1 mt-2">
                    <VChip
                      v-for="profile in getUserRoleProfiles(user).slice(0, 2)"
                      :key="profile"
                      size="x-small"
                      color="primary"
                      variant="tonal"
                    >
                      {{ profile }}
                    </VChip>
                    <VChip
                      v-if="getUserRoleProfiles(user).length > 2"
                      size="x-small"
                      color="secondary"
                      variant="tonal"
                    >
                      {{ ad("users.moreProfiles", { count: getUserRoleProfiles(user).length - 2 }) }}
                    </VChip>
                  </div>
                </div>
                <VIcon
                  :icon="Number(user.enabled ?? 1) === 1 ? 'tabler-circle-check' : 'tabler-circle-x'"
                  :color="Number(user.enabled ?? 1) === 1 ? 'success' : 'error'"
                />
              </button>

              <VAlert
                v-if="!filteredUsers.length && !loading"
                type="info"
                variant="tonal"
              >
                {{ ad("users.empty") }}
              </VAlert>
            </div>
          </VCardText>
        </VCard>

        <VCard class="designer-panel mt-4">
          <VCardText class="pa-5">
            <div class="d-flex align-center justify-space-between gap-3 mb-3">
              <div class="min-w-0">
                <h2 class="text-subtitle-1 font-weight-bold mb-1">
                  {{ ad("userPermissions.title") }}
                </h2>
                <p class="text-caption text-medium-emphasis mb-0">
                  {{ ad("userPermissions.subtitle") }}
                </p>
              </div>
              <div class="d-flex align-center gap-2">
                <VChip color="warning" variant="tonal" size="small">
                  {{ selectedUserPermissions.length }}
                </VChip>
                <VBtn
                  color="primary"
                  variant="tonal"
                  size="small"
                  icon="tabler-plus"
                  :disabled="!selectedUser"
                  :title="ad('userPermissions.add')"
                  @click="openUserPermissionDialog()"
                />
              </div>
            </div>

            <div
              v-if="selectedUserPermissions.length"
              class="user-permission-list"
            >
              <div
                v-for="permission in selectedUserPermissions"
                :key="permission.name"
                class="user-permission-card"
              >
                <VAvatar color="warning" variant="tonal" rounded size="34">
                  <VIcon icon="tabler-map-pin-lock" />
                </VAvatar>
                <div class="min-w-0 flex-grow-1">
                  <div class="font-weight-semibold text-truncate">
                    {{ displayUserPermissionValue(permission) }}
                  </div>
                  <div class="text-caption text-medium-emphasis text-truncate">
                    {{ permission.allow }}
                  </div>
                </div>
                <VChip size="x-small" color="secondary" variant="tonal">
                  {{
                    Number(permission.apply_to_all_doctypes ?? 0) === 1
                      ? ad("userPermissions.allTables")
                      : permission.applicable_for || ad("userPermissions.selectedTable")
                  }}
                </VChip>
                <VBtn
                  icon="tabler-pencil"
                  variant="text"
                  size="x-small"
                  color="secondary"
                  :title="ad('userPermissions.edit')"
                  @click="openUserPermissionDialog(permission)"
                />
                <VBtn
                  icon="tabler-trash"
                  variant="text"
                  size="x-small"
                  color="error"
                  :title="ad('userPermissions.remove')"
                  @click="openDeleteUserPermissionDialog(permission)"
                />
              </div>
            </div>

            <VAlert
              v-else
              type="info"
              variant="tonal"
            >
              {{ ad("userPermissions.empty") }}
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>

      <VCol cols="12" lg="6">
        <VCard class="designer-panel mb-6">
          <VCardText class="pa-5">
            <div class="d-flex flex-wrap align-center justify-space-between gap-3 mb-4">
              <div class="min-w-0">
                <h2 class="text-subtitle-1 font-weight-bold mb-1">
                  {{ ad("profiles.title") }}
                </h2>
                <p class="text-caption text-medium-emphasis mb-0">
                  {{ ad("profiles.subtitle") }}
                </p>
              </div>
              <div class="d-flex flex-wrap align-center gap-2">
                <VTextField
                  v-model="profileSearch"
                  :label="ad('profiles.search')"
                  prepend-inner-icon="tabler-search"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  clearable
                  hide-details
                  class="designer-input designer-compact-search"
                />
                <VBtn
                  color="primary"
                  prepend-icon="tabler-id-badge-2"
                  @click="openRoleProfileDialog"
                >
                  {{ ad("actions.newRoleProfile") }}
                </VBtn>
                <VBtn
                  color="primary"
                  variant="tonal"
                  prepend-icon="tabler-shield-plus"
                  @click="openRoleDialog"
                >
                  {{ ad("actions.newRole") }}
                </VBtn>
              </div>
            </div>

            <VRow dense>
              <VCol
                v-for="profile in paginatedRoleProfiles"
                :key="profile.name"
                cols="12"
                md="6"
              >
                <button
                  type="button"
                  class="profile-command-card"
                  :class="{ 'is-assigned': draftRoleProfiles.includes(getProfileName(profile)) }"
                  @click="toggleDraftProfile(profile)"
                  @contextmenu="openProfileContextMenu($event, profile)"
                >
                  <div class="d-flex align-start gap-3">
                    <VAvatar
                      :color="draftRoleProfiles.includes(getProfileName(profile)) ? 'primary' : 'secondary'"
                      variant="tonal"
                      rounded
                      size="42"
                    >
                      <VIcon icon="tabler-id-badge-2" />
                    </VAvatar>
                    <div class="min-w-0 flex-grow-1 text-start">
                      <div class="font-weight-semibold profile-title">
                        {{ getProfileName(profile) }}
                      </div>
                      <div class="text-caption text-medium-emphasis">
                        {{ ad("profiles.userCount", { count: profileUserCount(profile) }) }}
                      </div>
                    </div>
                    <VIcon
                      :icon="draftRoleProfiles.includes(getProfileName(profile)) ? 'tabler-check' : 'tabler-plus'"
                      :color="draftRoleProfiles.includes(getProfileName(profile)) ? 'primary' : 'secondary'"
                    />
                  </div>
                  <div class="d-flex flex-wrap gap-1 mt-4">
                    <VChip
                      v-for="role in getProfileRoles(profile).slice(0, 4)"
                      :key="role"
                      size="x-small"
                      color="secondary"
                      variant="tonal"
                      class="designer-chip"
                    >
                      {{ role }}
                    </VChip>
                    <VChip
                      v-if="getProfileRoles(profile).length > 4"
                      size="x-small"
                      color="secondary"
                      variant="outlined"
                    >
                      {{ ad("profiles.moreRoles", { count: getProfileRoles(profile).length - 4 }) }}
                    </VChip>
                  </div>
                </button>
              </VCol>
            </VRow>

            <div class="d-flex flex-wrap align-center justify-space-between gap-3 mt-4">
              <div class="text-caption text-medium-emphasis">
                {{ ad("profiles.page", { page: roleProfilePage, total: roleProfilePageCount }) }}
              </div>
              <VPagination
                v-model="roleProfilePage"
                :length="roleProfilePageCount"
                :total-visible="3"
                density="compact"
                rounded="0"
              />
            </div>
          </VCardText>
        </VCard>

        <VCard class="designer-panel mb-6">
          <VCardText class="pa-5">
            <div class="d-flex flex-wrap align-center justify-space-between gap-3 mb-4">
              <div class="min-w-0">
                <h2 class="text-subtitle-1 font-weight-bold mb-1">
                  {{ ad("pagePreview.title") }}
                </h2>
                <p class="text-caption text-medium-emphasis mb-0">
                  {{ ad("pagePreview.subtitle", { count: allowedPageCount }) }}
                </p>
              </div>
              <VAvatar color="success" variant="tonal" rounded>
                <VIcon icon="tabler-layout-sidebar" />
              </VAvatar>
            </div>

            <div class="page-preview-grid">
              <VCard
                v-for="page in pagePreviewCards.slice(0, 8)"
                :key="page.page_key"
                variant="outlined"
                class="page-preview-card"
                :class="{ 'is-allowed': page.allowed }"
              >
                <VCardText class="d-flex align-center gap-3">
                  <VAvatar
                    :color="page.allowed ? 'success' : 'secondary'"
                    variant="tonal"
                    rounded
                    size="38"
                  >
                    <VIcon :icon="pageIcon(page)" />
                  </VAvatar>
                  <div class="min-w-0 flex-grow-1">
                    <div class="font-weight-semibold text-truncate">
                      {{ page.label }}
                    </div>
                    <div class="text-caption text-medium-emphasis text-truncate">
                      {{ page.module_key || page.doctype || ad("pagePreview.general") }}
                    </div>
                  </div>
                  <VIcon
                    :icon="page.allowed ? 'tabler-lock-open' : 'tabler-lock'"
                    :color="page.allowed ? 'success' : 'secondary'"
                  />
                </VCardText>
              </VCard>
            </div>
          </VCardText>
        </VCard>

        <VCard class="designer-panel">
          <VCardText class="pa-5">
            <div class="d-flex flex-wrap align-center justify-space-between gap-3 mb-4">
              <div class="min-w-0">
                <h2 class="text-subtitle-1 font-weight-bold mb-1">
                  {{ ad("tablePreview.title") }}
                </h2>
                <p class="text-caption text-medium-emphasis mb-0">
                  {{ ad("tablePreview.subtitle") }}
                </p>
              </div>
              <div class="d-flex flex-wrap align-center gap-2">
                <VAutocomplete
                  v-model="selectedTable"
                  :items="permissionTables"
                  item-title="label"
                  item-value="value"
                  :label="ad('tablePreview.table')"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  clearable
                  hide-details
                  class="designer-input designer-compact-search"
                />
                <VAutocomplete
                  v-model="selectedTableRole"
                  :items="tableRoleOptions"
                  item-title="title"
                  item-value="value"
                  :label="ad('tablePreview.role')"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  hide-details
                  class="designer-input designer-compact-search"
                />
              </div>
            </div>

            <VAlert
              v-if="!selectedTableRole"
              type="info"
              variant="tonal"
              class="mb-4"
            >
              {{ ad("tablePreview.noRole") }}
            </VAlert>

            <div
              v-else
              class="table-review-strip mb-4"
            >
              <div class="d-flex align-center gap-3 min-w-0">
                <VAvatar color="primary" variant="tonal" rounded size="38">
                  <VIcon icon="tabler-table-options" />
                </VAvatar>
                <div class="min-w-0">
                  <div class="font-weight-semibold text-truncate">
                    {{ selectedTableLabel }}
                  </div>
                  <div class="text-caption text-medium-emphasis text-truncate">
                    {{ selectedTableRole }}
                  </div>
                </div>
              </div>
              <VChip color="primary" variant="tonal" size="small">
                {{ ad("tablePreview.enabledCount", { count: selectedRoleAllowedActionCount }) }}
              </VChip>
            </div>

            <VAlert
              v-if="lastPermissionChange"
              type="success"
              variant="tonal"
              class="mb-4"
            >
              {{
                ad("tablePreview.lastChange", {
                  action: am(`permissionTypes.${lastPermissionChange.action}`),
                  role: lastPermissionChange.role,
                  table: lastPermissionChange.table,
                  state: lastPermissionChange.enabled ? ad("tablePreview.on") : ad("tablePreview.off"),
                })
              }}
            </VAlert>

            <div class="table-action-grid">
              <VTooltip
                v-for="action in tablePermissionSummary"
                :key="action.key"
                location="top"
              >
                <template #activator="{ props }">
                  <button
                    v-bind="props"
                    type="button"
                    class="permission-action-button"
                    :class="{ 'is-allowed': action.allowed, 'is-muted': !action.allowed }"
                    :disabled="!selectedTable || !selectedTableRole || permissionSaving"
                    @click="toggleTablePermission(action.key)"
                  >
                    <VAvatar
                      :color="action.allowed ? action.color : 'secondary'"
                      variant="tonal"
                      size="34"
                      rounded
                    >
                      <VIcon :icon="action.icon" size="20" />
                    </VAvatar>
                    <div class="permission-action-label">
                      {{ am(`permissionTypes.${action.key}`) }}
                    </div>
                    <div
                      class="permission-state-pill"
                      :class="{ 'is-on': action.allowed }"
                    >
                      {{ action.allowed ? ad("tablePreview.on") : ad("tablePreview.off") }}
                    </div>
                    <VChip
                      v-if="action.ownerOnly"
                      size="x-small"
                      color="warning"
                      variant="flat"
                    >
                      {{ ad("tablePreview.ownerOnly") }}
                    </VChip>
                  </button>
                </template>
                <span>
                  {{ action.allowed ? ad("tablePreview.allowed") : ad("tablePreview.clickToUpdate") }}
                </span>
              </VTooltip>
            </div>

            <VExpansionPanels
              v-model="roleTablePanel"
              class="mt-4 role-table-dropdown"
              variant="accordion"
              @update:model-value="handleRoleTablePanel"
            >
              <VExpansionPanel>
                <VExpansionPanelTitle>
                  <div class="d-flex align-center gap-3 min-w-0">
                    <VAvatar color="primary" variant="tonal" rounded size="34">
                      <VIcon icon="tabler-table-filled" />
                    </VAvatar>
                    <div class="min-w-0">
                      <div class="font-weight-semibold text-truncate">
                        {{ roleTableSummaryTitle }}
                      </div>
                      <div class="text-caption text-medium-emphasis">
                        {{ ad("tablePreview.roleTablesSubtitle") }}
                      </div>
                    </div>
                  </div>
                </VExpansionPanelTitle>
                <VExpansionPanelText>
                  <VProgressLinear
                    v-if="roleTableSummaryLoading"
                    indeterminate
                    color="primary"
                    class="mb-4"
                  />

                  <div
                    v-if="roleTableSummaries.length"
                    class="role-table-list"
                  >
                    <div
                      v-for="row in roleTableSummaries"
                      :key="row.table"
                      class="role-table-row"
                    >
                      <div class="min-w-0">
                        <div class="font-weight-semibold text-truncate">
                          {{ row.label }}
                        </div>
                        <div class="text-caption text-medium-emphasis text-truncate">
                          {{ row.table }}
                        </div>
                      </div>
                      <div class="d-flex flex-wrap justify-end gap-1">
                        <VChip
                          v-for="action in row.actions"
                          :key="action"
                          size="x-small"
                          color="primary"
                          variant="tonal"
                        >
                          {{ am(`permissionTypes.${action}`) }}
                        </VChip>
                        <VChip
                          v-if="row.ownerOnly"
                          size="x-small"
                          color="warning"
                          variant="flat"
                        >
                          {{ ad("tablePreview.ownerOnly") }}
                        </VChip>
                      </div>
                    </div>
                  </div>

                  <VAlert
                    v-else-if="!roleTableSummaryLoading"
                    type="info"
                    variant="tonal"
                  >
                    {{ ad("tablePreview.noRoleTables") }}
                  </VAlert>
                </VExpansionPanelText>
              </VExpansionPanel>
            </VExpansionPanels>

            <VProgressLinear
              v-if="tableLoading"
              indeterminate
              color="primary"
              class="mt-4"
            />
          </VCardText>
        </VCard>
      </VCol>

      <VCol cols="12" lg="3">
        <VCard class="designer-panel designer-inspector">
          <VCardText v-if="selectedUser" class="pa-5">
            <div class="d-flex align-center gap-3 mb-4">
              <VAvatar size="48" color="primary" variant="tonal">
                <VImg
                  v-if="selectedUser.user_image"
                  :src="resolveFrappeFileUrl(selectedUser.user_image)"
                />
                <span v-else>{{ avatarInitial(getUserLabel(selectedUser)) }}</span>
              </VAvatar>
              <div class="min-w-0">
                <h2 class="text-subtitle-1 font-weight-bold mb-0 text-truncate">
                  {{ getUserLabel(selectedUser) }}
                </h2>
                <p class="text-body-2 text-medium-emphasis mb-0 text-truncate">
                  {{ selectedUser.email || selectedUser.name }}
                </p>
              </div>
            </div>

            <div class="inspector-field mb-5">
              <div class="inspector-field__head">
                <div class="d-flex align-center gap-2 min-w-0">
                  <VAvatar color="primary" variant="tonal" rounded size="26">
                    <VIcon icon="tabler-id-badge-2" size="16" />
                  </VAvatar>
                  <span class="inspector-field__title">{{ ad("inspector.roleProfiles") }}</span>
                </div>
                <VChip size="x-small" color="primary" variant="tonal">
                  {{ draftRoleProfiles.length }}
                </VChip>
              </div>
              <VAutocomplete
                v-model="draftRoleProfiles"
                :items="inspectorRoleProfileOptions"
                :placeholder="ad('inspector.roleProfilesPlaceholder')"
                prepend-inner-icon="tabler-search"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                multiple
                chips
                closable-chips
                clearable
                hide-details
                class="inspector-autocomplete"
              >
                <template #chip="{ props, item }">
                  <VChip
                    v-bind="props"
                    size="small"
                    color="primary"
                    variant="tonal"
                    label
                    :text="item.title"
                  />
                </template>
              </VAutocomplete>
            </div>

            <div class="inspector-field mb-5">
              <div class="inspector-field__head">
                <div class="d-flex align-center gap-2 min-w-0">
                  <VAvatar color="info" variant="tonal" rounded size="26">
                    <VIcon icon="tabler-shield" size="16" />
                  </VAvatar>
                  <span class="inspector-field__title">{{ ad("inspector.directRoles") }}</span>
                </div>
                <div class="d-flex align-center gap-2">
                  <VChip size="x-small" color="info" variant="tonal">
                    {{ draftRoles.length }}
                  </VChip>
                  <VBtn
                    variant="tonal"
                    color="primary"
                    size="x-small"
                    prepend-icon="tabler-shield-plus"
                    @click="openRoleDialog"
                  >
                    {{ ad("actions.newRole") }}
                  </VBtn>
                </div>
              </div>
              <VAutocomplete
                v-model="draftRoles"
                :items="roleOptions"
                :placeholder="ad('inspector.directRolesPlaceholder')"
                prepend-inner-icon="tabler-search"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                multiple
                chips
                closable-chips
                clearable
                hide-details
                class="inspector-autocomplete"
              >
                <template #chip="{ props, item }">
                  <VChip
                    v-bind="props"
                    size="small"
                    color="info"
                    variant="tonal"
                    label
                    :text="item.title"
                  />
                </template>
              </VAutocomplete>
            </div>

            <div class="mb-5">
              <div class="d-flex align-center justify-space-between gap-2 mb-3">
                <h3 class="text-subtitle-1 mb-0">
                  {{ ad("inspector.resolvedRoles") }}
                </h3>
                <VChip size="small" color="primary" variant="tonal">
                  {{ resolvedDraftRoles.length }}
                </VChip>
              </div>
              <div class="d-flex flex-wrap gap-2">
                <VChip
                  v-for="role in resolvedDraftRoles"
                  :key="role"
                  size="small"
                  color="primary"
                  variant="tonal"
                >
                  {{ role }}
                </VChip>
                <VChip
                  v-if="!resolvedDraftRoles.length"
                  size="small"
                  color="secondary"
                  variant="tonal"
                >
                  {{ ad("inspector.noRoles") }}
                </VChip>
              </div>
            </div>

            <div class="d-flex gap-2">
              <VBtn
                color="primary"
                prepend-icon="tabler-device-floppy"
                :loading="saving"
                :disabled="!hasChanges"
                class="flex-grow-1"
                @click="saveAssignments"
              >
                {{ ad("actions.save") }}
              </VBtn>
              <VBtn
                variant="tonal"
                color="secondary"
                icon="tabler-rotate"
                :title="ad('actions.revert')"
                :disabled="!hasChanges || saving"
                @click="revertDraft"
              />
            </div>
          </VCardText>

          <VCardText v-else>
            <VAlert type="info" variant="tonal">
              {{ ad("inspector.empty") }}
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <VDialog
      v-model="roleDialog"
      max-width="520"
      :fullscreen="isMobileShell"
      scrollable
    >
      <VCard>
        <VCardTitle class="d-flex align-center gap-3">
          <VAvatar color="primary" variant="tonal" rounded size="36">
            <VIcon icon="tabler-shield-plus" />
          </VAvatar>
          {{ ad("roleDialog.title") }}
        </VCardTitle>
        <VDivider />
        <VCardText>
          <VTextField
            v-model="roleForm.role_name"
            :label="ad('roleDialog.roleName')"
            prepend-inner-icon="tabler-shield"
            variant="outlined"
            density="comfortable"
            rounded="lg"
            hide-details
            class="designer-input mb-4"
          />
          <VSwitch
            v-model="roleForm.desk_access"
            :true-value="1"
            :false-value="0"
            color="primary"
            :label="ad('roleDialog.deskAccess')"
          />
          <VSwitch
            v-model="roleForm.disabled"
            :true-value="1"
            :false-value="0"
            color="error"
            :label="ad('roleDialog.disabled')"
          />
        </VCardText>
        <VDivider />
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="roleDialog = false"
          >
            {{ ad("actions.cancel") }}
          </VBtn>
          <VBtn
            color="primary"
            prepend-icon="tabler-device-floppy"
            :loading="roleSaving"
            @click="saveRole"
          >
            {{ ad("actions.save") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog
      v-model="roleProfileDialog"
      max-width="560"
      :fullscreen="isMobileShell"
      scrollable
    >
      <VCard>
        <VCardTitle class="d-flex align-center gap-3">
          <VAvatar color="primary" variant="tonal" rounded size="36">
            <VIcon icon="tabler-id-badge-2" />
          </VAvatar>
          {{ editingRoleProfile ? ad("roleProfileDialog.editTitle") : ad("roleProfileDialog.title") }}
        </VCardTitle>
        <VDivider />
        <VCardText>
          <VTextField
            v-model="roleProfileForm.role_profile"
            :label="ad('roleProfileDialog.name')"
            prepend-inner-icon="tabler-id-badge-2"
            variant="outlined"
            density="comfortable"
            rounded="lg"
            hide-details
            :readonly="Boolean(editingRoleProfile)"
            class="designer-input mb-4"
          />
          <VAutocomplete
            v-model="roleProfileForm.roles"
            :items="roleOptions"
            :label="ad('roleProfileDialog.roles')"
            :placeholder="ad('roleProfileDialog.rolesPlaceholder')"
            prepend-inner-icon="tabler-shield"
            variant="outlined"
            density="comfortable"
            rounded="lg"
            multiple
            chips
            closable-chips
            clearable
            hide-details
            class="designer-input"
          >
            <template #chip="{ props, item }">
              <VChip
                v-bind="props"
                size="small"
                color="primary"
                variant="tonal"
                label
                :text="item.title"
              />
            </template>
          </VAutocomplete>
        </VCardText>
        <VDivider />
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="roleProfileDialog = false"
          >
            {{ ad("actions.cancel") }}
          </VBtn>
          <VBtn
            color="primary"
            prepend-icon="tabler-device-floppy"
            :loading="roleProfileSaving"
            @click="saveRoleProfile"
          >
            {{ ad("actions.save") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VMenu
      v-model="profileMenu"
      :style="{ position: 'fixed', left: `${profileMenuX}px`, top: `${profileMenuY}px` }"
      :close-on-content-click="true"
    >
      <VList density="compact" min-width="160">
        <VListItem
          prepend-icon="tabler-edit"
          :title="ad('profiles.edit')"
          @click="openEditRoleProfileDialog(profileMenuTarget)"
        />
      </VList>
    </VMenu>

    <VDialog
      :fullscreen="isMobileShell"
      v-model="userPermissionDialog"
      max-width="640"
      scrollable
    >
      <VCard>
        <VCardTitle class="d-flex align-center gap-3">
          <VAvatar color="warning" variant="tonal" rounded size="36">
            <VIcon icon="tabler-map-pin-lock" />
          </VAvatar>
          {{ editingUserPermission ? ad("userPermissions.editTitle") : ad("userPermissions.addTitle") }}
        </VCardTitle>
        <VDivider />
        <VCardText>
          <VTextField
            :model-value="getUserLabel(selectedUser ?? ({} as FrappeUser))"
            :label="am('labels.user')"
            prepend-inner-icon="tabler-user"
            variant="outlined"
            density="comfortable"
            rounded="lg"
            readonly
            hide-details
            class="designer-input mb-4"
          />
          <FrappeDoctypeAutocomplete
            v-model="allowedTableOption"
            doctype="DocType"
            :label="am('labels.allowedTable')"
            :placeholder="ad('userPermissions.allowedTablePlaceholder')"
            order-by="name asc"
            density="comfortable"
            :hide-details="false"
            class="designer-input mb-4"
          />
          <FrappeDoctypeAutocomplete
            v-model="allowedValueOption"
            :doctype="userPermissionForm.allow || 'Warehouse'"
            :label="am('labels.allowedValue')"
            :placeholder="ad('userPermissions.allowedValuePlaceholder')"
            :disabled="!userPermissionForm.allow"
            :label-field="allowedValueLabelField"
            :fields="[allowedValueLabelField]"
            :search-fields="allowedValueSearchFields"
            density="comfortable"
            :hide-details="false"
            class="designer-input mb-4"
          />
          <VAlert
            type="info"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            {{ am("userPermissionHelp") }}
          </VAlert>
          <VCheckbox
            v-model="userPermissionForm.apply_to_all_doctypes"
            :true-value="1"
            :false-value="0"
            :label="am('labels.allTables')"
          />
        </VCardText>
        <VDivider />
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="userPermissionDialog = false"
          >
            {{ ad("actions.cancel") }}
          </VBtn>
          <VBtn
            color="primary"
            prepend-icon="tabler-device-floppy"
            :loading="userPermissionSaving"
            @click="saveUserPermission"
          >
            {{ ad("actions.save") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog
      v-model="deleteUserPermissionDialog"
      max-width="480"
    >
      <VCard>
        <VCardTitle class="d-flex align-center gap-3">
          <VAvatar color="error" variant="tonal" rounded size="36">
            <VIcon icon="tabler-trash" />
          </VAvatar>
          {{ ad("userPermissions.remove") }}
        </VCardTitle>
        <VCardText>
          {{
            ad("userPermissions.removeConfirm", {
              value: displayUserPermissionValue(userPermissionToDelete),
              user: userPermissionToDelete?.user,
            })
          }}
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="deleteUserPermissionDialog = false"
          >
            {{ ad("actions.cancel") }}
          </VBtn>
          <VBtn
            color="error"
            prepend-icon="tabler-trash"
            :loading="userPermissionDeleting"
            @click="confirmDeleteUserPermission"
          >
            {{ ad("userPermissions.remove") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>

<style scoped lang="scss">
.access-designer {
  .designer-panel,
  .access-designer-stat {
    border: 1px solid rgba(var(--v-border-color), 0.22);
    border-radius: 8px;
  }
}

.designer-hero {
  padding-block-start: 0.25rem;
}

.designer-subtitle {
  max-inline-size: 760px;
}

.designer-user-list {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  max-block-size: 560px;
  overflow: auto;
  padding-inline-end: 0.25rem;
}

.user-permission-list,
.role-table-list {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.user-permission-card,
.role-table-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid rgba(var(--v-border-color), 0.22);
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.018);
  gap: 0.75rem;
  padding: 0.7rem;
}

.user-permission-card {
  justify-content: flex-start;
}

.role-table-dropdown {
  :deep(.v-expansion-panel) {
    border: 1px solid rgba(var(--v-border-color), 0.22);
    border-radius: 8px !important;
    box-shadow: none;
  }
}

.designer-user-card,
.profile-command-card {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  inline-size: 100%;
  padding: 0.75rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.designer-user-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.designer-user-card:hover,
.profile-command-card:hover,
.designer-user-card.is-selected,
.profile-command-card.is-assigned {
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 8px 20px rgba(var(--v-theme-primary), 0.1);
  transform: translateY(-1px);
}

.profile-command-card {
  min-block-size: 126px;
}

.profile-title {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-height: 1.25;
}

.designer-chip {
  max-inline-size: 10rem;
}

.designer-chip :deep(.v-chip__content) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.designer-input {
  :deep(.v-field) {
    background: rgb(var(--v-theme-surface));
  }

  :deep(.v-field--focused) {
    box-shadow: 0 0 0 3px rgba(var(--v-theme-primary), 0.08);
  }
}

.designer-compact-search {
  max-inline-size: 260px;
  min-inline-size: 220px;
}

.page-preview-grid,
.table-action-grid {
  display: grid;
  gap: 0.75rem;
}

.page-preview-grid {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.table-action-grid {
  grid-template-columns: repeat(auto-fit, minmax(104px, 1fr));
}

.table-review-strip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid rgba(var(--v-theme-primary), 0.22);
  border-radius: 8px;
  background: rgba(var(--v-theme-primary), 0.045);
  gap: 0.75rem;
  padding: 0.75rem;
}

.page-preview-card,
.permission-action-button {
  border-radius: 8px;
}

.page-preview-card.is-allowed {
  border-color: rgba(var(--v-theme-success), 0.45);
}

.permission-action-button {
  display: flex;
  min-block-size: 92px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(var(--v-border-color), 0.26);
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  gap: 0.35rem;
  padding: 0.7rem 0.55rem;
  text-align: center;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.permission-action-button:not(:disabled):hover,
.permission-action-button.is-allowed {
  border-color: rgba(var(--v-theme-primary), 0.5);
  box-shadow: 0 8px 18px rgba(var(--v-theme-primary), 0.1);
}

.permission-action-button:not(:disabled):hover {
  transform: translateY(-1px);
}

.permission-action-button:disabled {
  cursor: not-allowed;
}

.permission-action-button.is-muted {
  opacity: 0.58;
}

.permission-action-label {
  overflow-wrap: anywhere;
  font-size: 0.76rem;
  font-weight: 600;
  line-height: 1.1;
}

.permission-state-pill {
  border-radius: 999px;
  background: rgba(var(--v-theme-on-surface), 0.08);
  color: rgba(var(--v-theme-on-surface), 0.7);
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1;
  padding: 0.28rem 0.45rem;
}

.permission-state-pill.is-on {
  background: rgba(var(--v-theme-success), 0.14);
  color: rgb(var(--v-theme-success));
}

.designer-inspector {
  position: sticky;
  inset-block-start: 1rem;
}

.inspector-field {
  border: 1px solid rgba(var(--v-border-color), 0.18);
  border-radius: 12px;
  background: rgba(var(--v-theme-on-surface), 0.015);
  padding: 0.85rem 0.85rem 0.95rem;
}

.inspector-field__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-block-end: 0.65rem;
}

.inspector-field__title {
  overflow: hidden;
  font-size: 0.82rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inspector-autocomplete {
  :deep(.v-field) {
    background: rgb(var(--v-theme-surface));
  }

  :deep(.v-field--focused) {
    box-shadow: 0 0 0 3px rgba(var(--v-theme-primary), 0.08);
  }
}

@media (max-width: 1279px) {
  .designer-inspector {
    position: static;
  }
}
</style>
